// MUI-style components used by the older portal pages. The API is unchanged;
// the look comes from the RandCards design system (src/design), so these pages match the rest of the portal.
import React, { Children, cloneElement, createContext, createElement, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, Info, WarningCircle, X } from '@phosphor-icons/react';
import {
  APP_THEME,
  BRAND,
  alpha,
  mergeStyles,
  resolveResponsiveValue,
  resolveSx,
  ThemeContextProvider,
  useAppTheme,
  useMediaQuery as useResponsiveQuery,
} from './theme';

export { alpha };

function cx(...names) {
  return names.filter(Boolean).join(' ');
}

export function useTheme() {
  return useAppTheme();
}

export function createTheme(themeConfig = {}) {
  return {
    ...APP_THEME,
    ...themeConfig,
  };
}

export function ThemeProvider({ theme, children }) {
  return <ThemeContextProvider theme={theme}>{children}</ThemeContextProvider>;
}

export function CssBaseline() {
  return null;
}

function useBaseStyle({ style, sx }, defaults = {}) {
  const theme = useAppTheme();
  return useMemo(() => mergeStyles(defaults, style, resolveSx(sx, theme)), [defaults, style, sx, theme]);
}

function renderAs(component, props, children) {
  return createElement(component || 'div', props, children);
}

export function Box({ component = 'div', style, sx, children, ...rest }) {
  return renderAs(component, { ...rest, style: useBaseStyle({ style, sx }) }, children);
}

export function Container({ maxWidth = '1200px', style, sx, children, ...rest }) {
  const widths = { xs: '480px', sm: '640px', md: '960px', lg: '1200px', xl: '1440px' };
  return (
    <Box
      {...rest}
      style={mergeStyles({ width: '100%', maxWidth: widths[maxWidth] || maxWidth, margin: '0 auto' }, style)}
      sx={sx}
    >
      {children}
    </Box>
  );
}

export function Paper({ style, sx, children, elevation, className, ...rest }) {
  void elevation;
  return (
    <Box {...rest} className={cx('rc-paper', className)} style={style} sx={sx}>
      {children}
    </Box>
  );
}

export function Card(props) {
  return <Paper {...props} />;
}

export function CardContent({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} style={mergeStyles({ padding: 24 }, style)} sx={sx}>
      {children}
    </Box>
  );
}

export function CardActions({ children, style, sx, ...rest }) {
  return (
    <Box
      {...rest}
      style={mergeStyles({ display: 'flex', alignItems: 'center', gap: 12, padding: 16, flexWrap: 'wrap' }, style)}
      sx={sx}
    >
      {children}
    </Box>
  );
}

export function CardHeader({ title, subheader, action, style, sx, ...rest }) {
  return (
    <Box
      {...rest}
      style={mergeStyles(
        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, padding: 24, paddingBottom: 0, flexWrap: 'wrap' },
        style
      )}
      sx={sx}
    >
      <Box>
        {typeof title === 'string' ? <Typography variant="h6">{title}</Typography> : title}
        {subheader ? (
          typeof subheader === 'string' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subheader}
            </Typography>
          ) : (
            subheader
          )
        ) : null}
      </Box>
      {action}
    </Box>
  );
}

// Tailwind steps only; weights capped at bold; no all caps.
const TYPOGRAPHY_VARIANTS = {
  h1: { tag: 'h1', fontSize: '36px', fontWeight: 700, lineHeight: '40px', letterSpacing: '-0.025em' },
  h2: { tag: 'h2', fontSize: '30px', fontWeight: 600, lineHeight: '36px', letterSpacing: '-0.02em' },
  h3: { tag: 'h3', fontSize: '24px', fontWeight: 600, lineHeight: '32px', letterSpacing: '-0.02em' },
  h4: { tag: 'h4', fontSize: '20px', fontWeight: 600, lineHeight: '28px', letterSpacing: '-0.01em' },
  h5: { tag: 'h5', fontSize: '18px', fontWeight: 600, lineHeight: '28px', letterSpacing: '-0.01em' },
  h6: { tag: 'h6', fontSize: '16px', fontWeight: 600, lineHeight: '24px' },
  subtitle1: { tag: 'p', fontSize: '16px', fontWeight: 500, lineHeight: '24px' },
  subtitle2: { tag: 'p', fontSize: '14px', fontWeight: 600, lineHeight: '20px' },
  body1: { tag: 'p', fontSize: '16px', fontWeight: 400, lineHeight: '24px' },
  body2: { tag: 'p', fontSize: '14px', fontWeight: 400, lineHeight: '20px' },
  caption: { tag: 'span', fontSize: '12px', fontWeight: 500, lineHeight: '16px' },
  overline: { tag: 'span', fontSize: '12px', fontWeight: 500, lineHeight: '16px', letterSpacing: '0.02em' },
};

function colorValue(color) {
  if (!color) return BRAND.text;
  if (color === 'text.secondary' || color === 'textSecondary') return BRAND.textSoft;
  if (color === 'text.primary' || color === 'textPrimary') return BRAND.text;
  if (color === 'error' || color === 'error.main') return BRAND.error;
  if (color === 'success' || color === 'success.main') return BRAND.success;
  if (color === 'warning' || color === 'warning.main') return BRAND.warning;
  if (color === 'primary' || color === 'primary.main') return BRAND.text;
  return color;
}

export function Typography({
  component,
  variant = 'body1',
  color,
  align,
  gutterBottom,
  style,
  sx,
  children,
  ...rest
}) {
  const config = TYPOGRAPHY_VARIANTS[variant] || TYPOGRAPHY_VARIANTS.body1;
  const tag = component || config.tag;
  const { tag: _tag, ...typeStyle } = config;
  void _tag;
  return renderAs(
    tag,
    {
      ...rest,
      style: useBaseStyle(
        {
          style: mergeStyles(
            {
              margin: 0,
              color: colorValue(color),
              textAlign: align,
              ...(gutterBottom ? { marginBottom: '8px' } : null),
              ...typeStyle,
            },
            style
          ),
          sx,
        },
        {}
      ),
    },
    children
  );
}

function buttonClass(variant, color, size) {
  const tone = color === 'error' ? 'rc-btn--danger'
    : variant === 'text' ? 'rc-btn--ghost'
      : variant === 'outlined' ? ''
        : color === 'inherit' || color === 'secondary' ? ''
          : 'rc-btn--primary';
  return cx('rc-btn', tone, size === 'small' && 'rc-btn--sm');
}

export function Button({
  component,
  variant = 'contained',
  color,
  size = 'medium',
  fullWidth,
  startIcon,
  endIcon,
  disabled,
  className,
  style,
  sx,
  children,
  ...rest
}) {
  const Component = component || (rest.href ? 'a' : 'button');
  return renderAs(
    Component,
    {
      ...rest,
      disabled,
      'aria-disabled': disabled && Component !== 'button' ? true : undefined,
      className: cx(buttonClass(variant, color, size), fullWidth && 'rc-btn--block', className),
      style: useBaseStyle({ style: mergeStyles(disabled && Component !== 'button' ? { pointerEvents: 'none' } : null, style), sx }, {}),
    },
    <>
      {startIcon ? <span className="rc-btn__icon">{startIcon}</span> : null}
      <span>{children}</span>
      {endIcon ? <span className="rc-btn__icon">{endIcon}</span> : null}
    </>
  );
}

export function IconButton({ component, children, size, className, style, sx, ...rest }) {
  const Component = component || 'button';
  return renderAs(
    Component,
    {
      ...rest,
      className: cx('rc-btn rc-btn--icon', size === 'small' && 'rc-btn--sm', className),
      style: useBaseStyle({ style, sx }, {}),
    },
    children
  );
}

// Loading indicator for buttons and small areas. Pages should prefer skeletons for whole sections.
export function CircularProgress({ size = 18, style, sx, ...rest }) {
  return (
    <span
      {...rest}
      role="progressbar"
      aria-label={rest['aria-label'] || 'Loading'}
      className="rc-loader"
      style={useBaseStyle({ style: mergeStyles({ '--rc-loader-size': `${Math.max(12, Number(size) || 18)}px` }, style), sx })}
    >
      <span />
      <span />
      <span />
    </span>
  );
}

const SelectContext = createContext(false);
const TableHeadContext = createContext(false);

function childLabel(child) {
  if (!child) return '';
  if (typeof child === 'string' || typeof child === 'number') return String(child);
  if (Array.isArray(child)) return child.map((item) => childLabel(item)).join('');
  if (child.props?.children) return childLabel(child.props.children);
  return '';
}

export function MenuItem(props) {
  const inSelect = useContext(SelectContext);

  if (inSelect) {
    return (
      <option value={props.value} disabled={props.disabled}>
        {childLabel(props.children)}
      </option>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      className="rc-menu-item"
      style={mergeStyles(props.style, resolveSx(props.sx))}
    >
      {props.children}
    </button>
  );
}

export function Select({ children, native, className, style, sx, value, onChange, ...rest }) {
  void native;
  return (
    <SelectContext.Provider value>
      <select
        {...rest}
        className={cx('rc-select', className)}
        value={value}
        onChange={onChange}
        style={useBaseStyle({ style, sx })}
      >
        {children}
      </select>
    </SelectContext.Provider>
  );
}

export function InputAdornment({ position, children }) {
  void position;
  return <span className="rc-input__adornment">{children}</span>;
}

export function TextField({
  label,
  helperText,
  error,
  select,
  multiline,
  rows = 4,
  fullWidth,
  value,
  onChange,
  InputProps,
  inputProps,
  style,
  sx,
  children,
  id,
  ...rest
}) {
  const wrapperStyle = useBaseStyle(
    {
      style: mergeStyles({ width: fullWidth ? '100%' : undefined }, style),
      sx,
    },
    {}
  );
  const fieldId = useMemo(() => id || `rc-field-${Math.random().toString(36).slice(2, 9)}`, [id]);

  const control = select ? (
    <SelectContext.Provider value>
      <div className="rc-input">
        {InputProps?.startAdornment}
        <select {...rest} {...inputProps} id={fieldId} value={value} onChange={onChange}>
          {children}
        </select>
        {InputProps?.endAdornment}
      </div>
    </SelectContext.Provider>
  ) : multiline ? (
    <div className="rc-input rc-input--multiline">
      {InputProps?.startAdornment}
      <textarea {...rest} {...inputProps} id={fieldId} rows={rows} value={value} onChange={onChange} style={{ minHeight: rows * 20 }} />
      {InputProps?.endAdornment}
    </div>
  ) : (
    <div className="rc-input">
      {InputProps?.startAdornment}
      <input {...rest} {...inputProps} id={fieldId} value={value} onChange={onChange} />
      {InputProps?.endAdornment}
    </div>
  );

  return (
    <div className="rc-field" style={wrapperStyle}>
      {label ? <label className="rc-label" htmlFor={fieldId}>{label}</label> : null}
      {control}
      {helperText ? <div className={cx('rc-help', error && 'rc-help--error')}>{helperText}</div> : null}
    </div>
  );
}

export function FormControl({ children, fullWidth, style, sx, ...rest }) {
  return (
    <Box {...rest} className="rc-field" style={mergeStyles({ width: fullWidth ? '100%' : undefined }, style)} sx={sx}>
      {children}
    </Box>
  );
}

export function InputLabel({ children, style, sx, ...rest }) {
  return (
    <label {...rest} className="rc-label" style={useBaseStyle({ style, sx }, {})}>
      {children}
    </label>
  );
}

export function ButtonGroup({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} style={mergeStyles({ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }, style)} sx={sx}>
      {children}
    </Box>
  );
}

const CHIP_TONES = { primary: 'accent', secondary: '', success: 'success', warning: 'warning', error: 'danger', info: '', default: '' };

export function Chip({ label, size = 'medium', color, variant, onClick, onDelete, icon, className, style, sx, ...rest }) {
  void size;
  void variant;
  const finalStyle = useBaseStyle({ style, sx }, {});
  if (onClick) {
    return (
      <button type="button" {...rest} onClick={onClick} className={cx('rc-chip', className)} style={finalStyle}>
        {icon}
        {label}
      </button>
    );
  }
  const tone = CHIP_TONES[color] || '';
  return (
    <span {...rest} className={cx('rc-badge', tone && `rc-badge--${tone}`, className)} style={finalStyle}>
      {icon}
      {label}
      {onDelete ? (
        <button type="button" className="rc-badge__remove" onClick={onDelete} aria-label={`Remove ${childLabel(label)}`}>
          <X size={12} aria-hidden="true" />
        </button>
      ) : null}
    </span>
  );
}

export function Divider({ orientation = 'horizontal', style, sx, ...rest }) {
  const isVertical = orientation === 'vertical';
  return (
    <Box
      {...rest}
      role="separator"
      style={mergeStyles(
        isVertical
          ? { width: 1, alignSelf: 'stretch', background: BRAND.border }
          : { height: 1, width: '100%', background: BRAND.border },
        style
      )}
      sx={sx}
    />
  );
}

const ALERT_ICONS = { success: CheckCircle, error: WarningCircle, warning: WarningCircle, info: Info };

export function Alert({ severity = 'info', children, className, style, sx, onClose, action, icon, ...rest }) {
  const tone = ALERT_ICONS[severity] ? severity : 'info';
  const Icon = ALERT_ICONS[tone];
  return (
    <Box
      {...rest}
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx('rc-alert', `rc-alert--${tone}`, className)}
      style={style}
      sx={sx}
    >
      {icon === false ? null : icon || <Icon size={20} aria-hidden="true" />}
      <div className="rc-alert__body">{children}</div>
      {action}
      {onClose ? (
        <button type="button" className="rc-alert__close" onClick={onClose} aria-label="Dismiss">
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
    </Box>
  );
}

export function Snackbar({ open, children }) {
  if (!open || typeof document === 'undefined') return null;
  return createPortal(<div className="rc-snackbar">{children}</div>, document.body);
}

export function Fade({ in: open, children }) {
  if (!open) return null;
  return children;
}

export function Collapse({ in: open, children }) {
  if (!open) return null;
  return children;
}

function portalRoot(children) {
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}

export function Dialog({ open, children, onClose, maxWidth = 'md', fullWidth }) {
  if (!open) return null;
  const size = fullWidth ? 'rc-dialog--xl' : { xs: '', sm: '', md: 'rc-dialog--md', lg: 'rc-dialog--lg', xl: 'rc-dialog--xl' }[maxWidth] || 'rc-dialog--md';
  return portalRoot(
    <div className="rc-backdrop" onClick={onClose}>
      <div className={cx('rc-dialog', size)} role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function DialogTitle({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} className="rc-dialog__head" style={style} sx={sx}>
      {children}
    </Box>
  );
}

export function DialogContent({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} className="rc-dialog__body" style={style} sx={sx}>
      {children}
    </Box>
  );
}

export function DialogActions({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} className="rc-dialog__actions" style={style} sx={sx}>
      {children}
    </Box>
  );
}

export function Drawer({ open, anchor = 'left', children, onClose, style, sx }) {
  if (!open) return null;
  return portalRoot(
    <div className="rc-backdrop rc-drawer-backdrop" onClick={onClose}>
      <div
        className={cx('rc-drawer', anchor === 'right' && 'rc-drawer--right')}
        style={mergeStyles(style, resolveSx(sx))}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function Menu({ open, anchorEl, onClose, children }) {
  if (!open || typeof document === 'undefined') return null;

  const rect = anchorEl?.getBoundingClientRect?.();
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 'var(--rc-z-menu)' }} onClick={onClose}>
      <div
        className="rc-menu"
        style={{
          position: 'absolute',
          top: rect ? rect.bottom + 8 : 72,
          left: rect ? Math.max(12, Math.min(rect.left, window.innerWidth - 232)) : 'auto',
          right: rect ? 'auto' : 12,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

const GridContext = createContext(false);

export function Grid({ container, item, spacing = 0, xs, sm, md, lg, xl, children, style, sx, ...rest }) {
  const inContainer = useContext(GridContext);
  const span = resolveResponsiveValue({ xs, sm, md, lg, xl }) || 12;

  if (container) {
    return (
      <GridContext.Provider value>
        <Box
          {...rest}
          style={mergeStyles(
            { display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: `${spacing * 8}px` },
            style
          )}
          sx={sx}
        >
          {children}
        </Box>
      </GridContext.Provider>
    );
  }

  if (item || inContainer) {
    return (
      <Box {...rest} style={mergeStyles({ gridColumn: `span ${Math.min(Number(span) || 12, 12)}`, minWidth: 0 }, style)} sx={sx}>
        {children}
      </Box>
    );
  }

  return (
    <Box {...rest} style={style} sx={sx}>
      {children}
    </Box>
  );
}

export function Tooltip({ title, children }) {
  if (!React.isValidElement(children)) return children;
  return cloneElement(children, { title: childLabel(title) || title || undefined });
}

export function Avatar({ src, alt, children, style, sx, ...rest }) {
  const base = useBaseStyle({ style, sx }, {});
  if (src) {
    return <img {...rest} src={src} alt={alt || ''} className="rc-avatar" style={base} />;
  }
  return (
    <span {...rest} className="rc-avatar rc-avatar--initials" style={base}>
      {children}
    </span>
  );
}

export function List({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} component="div" style={mergeStyles({ display: 'grid', gap: 8, padding: 0, margin: 0 }, style)} sx={sx}>
      {children}
    </Box>
  );
}

export function ListItem({ children, disablePadding, style, sx, ...rest }) {
  return (
    <Box {...rest} component="div" style={mergeStyles({ padding: disablePadding ? 0 : undefined }, style)} sx={sx}>
      {children}
    </Box>
  );
}

export function ListItemButton({ children, selected, style, sx, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      className={cx('rc-list-button', selected && 'is-active')}
      aria-current={selected ? 'true' : undefined}
      style={useBaseStyle({ style, sx }, {})}
    >
      {children}
    </button>
  );
}

export function ListItemIcon({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} style={mergeStyles({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }, style)} sx={sx}>
      {children}
    </Box>
  );
}

export function ListItemAvatar(props) {
  return <ListItemIcon {...props} />;
}

export function ListItemText({ primary, secondary, style, sx, ...rest }) {
  return (
    <Box {...rest} style={style} sx={sx}>
      {primary !== undefined ? <Typography variant="body2">{primary}</Typography> : null}
      {secondary !== undefined ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {secondary}
        </Typography>
      ) : null}
    </Box>
  );
}

export function Checkbox({ checked, onChange, disabled, style, sx, ...rest }) {
  return (
    <input
      {...rest}
      type="checkbox"
      checked={Boolean(checked)}
      onChange={onChange}
      disabled={disabled}
      className="rc-check"
      style={useBaseStyle({ style, sx }, {})}
    />
  );
}

export function Switch({ checked, onChange, disabled, style, sx, ...rest }) {
  return (
    <input
      {...rest}
      type="checkbox"
      role="switch"
      checked={Boolean(checked)}
      onChange={onChange}
      disabled={disabled}
      className="rc-switch"
      style={useBaseStyle({ style, sx }, {})}
    />
  );
}

export function FormControlLabel({ control, label, style, sx, ...rest }) {
  return (
    <label {...rest} className="rc-check-label" style={useBaseStyle({ style, sx }, {})}>
      {control}
      <span>{label}</span>
    </label>
  );
}

export function TableContainer({ children, style, sx, ...rest }) {
  return (
    <Box {...rest} className="rc-table-wrap rc-table-wrap--panel" style={style} sx={sx}>
      {children}
    </Box>
  );
}

export function Table({ children, className, style, sx, ...rest }) {
  return (
    <table {...rest} className={cx('rc-table', className)} style={useBaseStyle({ style, sx })}>
      {children}
    </table>
  );
}

export function TableHead({ children, ...rest }) {
  return (
    <TableHeadContext.Provider value>
      <thead {...rest}>{children}</thead>
    </TableHeadContext.Provider>
  );
}

export function TableBody({ children, ...rest }) {
  return <tbody {...rest}>{children}</tbody>;
}

export function TableRow({ children, hover, selected, style, sx, ...rest }) {
  void hover;
  return (
    <tr {...rest} className={selected ? 'is-selected' : undefined} style={useBaseStyle({ style, sx }, {})}>
      {children}
    </tr>
  );
}

export function TableCell({ children, align, style, sx, ...rest }) {
  const isHeader = useContext(TableHeadContext);
  const Component = isHeader ? 'th' : 'td';
  return renderAs(
    Component,
    {
      ...rest,
      className: align === 'right' ? 'rc-num' : undefined,
      style: useBaseStyle({ style: mergeStyles({ textAlign: align || 'left' }, style), sx }, {}),
    },
    children
  );
}

export function Tabs({ value, onChange, children, style, sx, ...rest }) {
  const items = Children.toArray(children);
  return (
    <Box {...rest} role="tablist" className="rc-tabs" style={style} sx={sx}>
      {items.map((child) =>
        cloneElement(child, {
          selected: child.props.value === value,
          onSelect: () => onChange?.(null, child.props.value),
        })
      )}
    </Box>
  );
}

export function Tab({ label, icon, selected, onSelect, style, sx, ...rest }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={Boolean(selected)}
      {...rest}
      onClick={onSelect}
      className="rc-tab rc-row"
      style={useBaseStyle({ style: mergeStyles({ gap: 8, flexWrap: 'nowrap' }, style), sx }, {})}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export function BottomNavigation({ value, onChange, children, style, sx, ...rest }) {
  const items = Children.toArray(children);
  return (
    <Box
      {...rest}
      className="rc-bottom-nav"
      style={mergeStyles({ gridTemplateColumns: `repeat(${Math.max(items.length, 1)}, minmax(0, 1fr))` }, style)}
      sx={sx}
    >
      {items.map((child) =>
        cloneElement(child, {
          selected: child.props.value === value,
          onSelect: () => onChange?.(null, child.props.value),
        })
      )}
    </Box>
  );
}

export function BottomNavigationAction({ label, icon, selected, onSelect, style, sx, ...rest }) {
  return (
    <button
      type="button"
      aria-pressed={Boolean(selected)}
      {...rest}
      onClick={onSelect}
      className="rc-bottom-nav__item"
      style={useBaseStyle({ style, sx }, {})}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export function Slider({ value, onChange, min = 0, max = 100, step = 1, style, sx, ...rest }) {
  return (
    <input
      {...rest}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange?.(event, Number(event.target.value))}
      style={useBaseStyle({ style: mergeStyles({ width: '100%' }, style), sx })}
    />
  );
}

export function Pagination({ page = 1, count = 1, onChange, style, sx, ...rest }) {
  const items = Array.from({ length: count }, (_, index) => index + 1);
  return (
    <Box {...rest} style={mergeStyles({ display: 'flex', gap: 8, flexWrap: 'wrap' }, style)} sx={sx}>
      {items.map((item) => (
        <button
          key={item}
          type="button"
          aria-current={item === page ? 'page' : undefined}
          className={cx('rc-btn rc-btn--sm', item === page && 'rc-btn--primary')}
          onClick={(event) => onChange?.(event, item)}
        >
          {item}
        </button>
      ))}
    </Box>
  );
}

export function ToggleButtonGroup({ value, onChange, children, style, sx, ...rest }) {
  const items = Children.toArray(children);
  const { exclusive, ...boxProps } = rest;
  return (
    <Box {...boxProps} role="group" className="rc-segmented" style={style} sx={sx}>
      {items.map((child) =>
        cloneElement(child, {
          selected: child.props.value === value || (Array.isArray(value) && value.includes(child.props.value)),
          onToggle: () => {
            if (exclusive) {
              onChange?.(null, child.props.value === value ? null : child.props.value);
              return;
            }

            const nextValue = Array.isArray(value) ? [...value] : [];
            if (nextValue.includes(child.props.value)) {
              onChange?.(null, nextValue.filter((item) => item !== child.props.value));
            } else {
              onChange?.(null, [...nextValue, child.props.value]);
            }
          },
        })
      )}
    </Box>
  );
}

export function ToggleButton({ selected, onToggle, children, style, sx, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      aria-pressed={Boolean(selected)}
      onClick={onToggle}
      style={useBaseStyle({ style, sx }, {})}
    >
      {children}
    </button>
  );
}

export function LinearProgress({ variant = 'indeterminate', value = 0, style, sx, ...rest }) {
  const determinate = variant === 'determinate';
  return (
    <Box
      {...rest}
      role="progressbar"
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? 100 : undefined}
      aria-valuenow={determinate ? Math.round(value) : undefined}
      className={cx('rc-progress', !determinate && 'rc-progress--indeterminate')}
      style={style}
      sx={sx}
    >
      <span style={determinate ? { transform: `scaleX(${Math.max(0, Math.min(100, value)) / 100})` } : undefined} />
    </Box>
  );
}

export function useMediaQuery(query) {
  return useResponsiveQuery(query);
}
